import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from "primereact/multiselect";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ToggleButton } from "primereact/togglebutton";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import { toast, ToastContainer } from "react-toastify";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { getAlpha2Code } from "i18n-iso-countries";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { API_BASE } from "../../utils/api";
import "./AddResturant.css";
countries.registerLocale(enLocale);

export default function RegisterRestaurant() {
  const logoUploadRef = useRef(null);
  const debounceTimer = useRef(null);
  const inputRefs = useRef({});

  const [step, setStep] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    category: "",
    cuisineTypes: [],
    description: "",
    licenseNumber: "",
    gstNumber: "",
    status: "Active",
    logo: null,
    logoPreview: null,
    address: "",
    pincode: "",
    city: "",
    state: "",
    country: "",
    coordinates: "",
    website: "",
    socialLinks: "",
    contactPerson: "",
    phone: "",
    email: "",
    hours: {},
  });

  const [categories, setCategories] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [message, setMessage] = useState("");
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [logoRemoved, setLogoRemoved] = useState(false);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [catRes, cuiRes] = await Promise.all([
          axios.get(`${API_BASE}/api/resturant/getCategories`),
          axios.get(`${API_BASE}/api/resturant/getCuisines`),
        ]);
        setCategories(catRes.data);
        setCuisines(cuiRes.data);
      } catch (err) {
        console.error("Error fetching categories or cuisines", err);
      }
    };

    const fetchRestaurant = async () => {
      if (!restaurantId) return;

      try {
        const res = await axios.get(
          `${API_BASE}/api/resturant/${restaurantId}`
        );
        const restaurantData = res.data;

        const transformedHours = {};
        Object.entries(restaurantData.hours || {}).forEach(([day, config]) => {
          const { openingTime, closingTime, ...rest } = config;

          const parseTime = (str) => {
            const [time, period] = str.split(" ");
            const [hour, minute] = time.split(":");
            return { hour: hour.padStart(2, "0"), minute, period };
          };

          transformedHours[day] = {
            ...rest,
            ...(openingTime ? { openingTime: parseTime(openingTime) } : {}),
            ...(closingTime ? { closingTime: parseTime(closingTime) } : {}),
          };
        });

        setForm((prev) => ({
          ...prev,
          ...restaurantData,
          hours: transformedHours,
          logoPreview: restaurantData.logoUrl
            ? `${API_BASE}${restaurantData.logoUrl}`
            : null,
          logo: null,
        }));
      } catch (err) {
        toast.error("❌ Failed to load restaurant details.");
      }
    };

    const savedData = JSON.parse(sessionStorage.getItem("restaurantForm"));
    if (savedData) setForm((prev) => ({ ...prev, ...savedData }));

    fetchOptions();
    fetchRestaurant();
  }, [restaurantId]);

  const validateStep = (currentStep) => {
    const errors = {};
    const updatedForm = { ...form };

    if (currentStep === 0) {
      if (!form.name.trim()) errors.name = "Name is required";
      if (!form.category) errors.category = "Category is required";
      if (!form.cuisineTypes.length)
        errors.cuisineTypes = "Select at least one cuisine";
    }

    if (currentStep === 1) {
      if (!form.address.trim()) errors.address = "Address is required";
      if (!form.pincode.match(/^\d{5,6}$/)) errors.pincode = "Invalid pincode";
      if (!form.city.trim()) errors.city = "City is required";
      if (!form.state.trim()) errors.state = "State is required";
      if (!form.country.trim()) errors.country = "Country is required";
    }

    if (currentStep === 2) {
      // Contact person
      if (!form.contactPerson.trim()) {
        errors.contactPerson = "Contact person is required";
      } else if (!/^[A-Za-z ]+$/.test(form.contactPerson.trim())) {
        errors.contactPerson = "Only letters and spaces allowed";
      }

      // Phone
      try {
        const isoCode = getAlpha2Code(form.country, "en");
        const phoneNumber = parsePhoneNumberFromString(form.phone, isoCode);
        if (!phoneNumber || !phoneNumber.isValid()) {
          errors.phone = `Invalid phone number for ${form.country}`;
        } else {
          updatedForm.phone = phoneNumber.number;
          form.phone = phoneNumber.number;
        }
      } catch (e) {
        errors.phone = "Invalid phone number format";
      }

      // Email
      if (!form.email.match(/^\S+@\S+\.\S+$/)) {
        errors.email = "Email is invalid";
      }

      // Operating Hours
      const hours = form.hours || {};
      const openDays = Object.values(hours).filter((day) => day?.open);
      if (openDays.length === 0) {
        errors.hours = "At least one open day must be selected.";
      } else {
        const formattedHours = {};
        for (const [day, config] of Object.entries(hours)) {
          if (config?.open) {
            const result = { open: true };

            if (config.is24HoursOpen) {
              result.is24HoursOpen = true;
            } else {
              const opening = config.openingTime || {};
              const closing = config.closingTime || {};
              const valid =
                opening.hour &&
                opening.minute &&
                opening.period &&
                closing.hour &&
                closing.minute &&
                closing.period;

              if (!valid) {
                errors.hours = `Incomplete time for ${day}`;
                continue;
              }

              result.openingTime = `${opening.hour}:${opening.minute} ${opening.period}`;
              result.closingTime = `${closing.hour}:${closing.minute} ${closing.period}`;
              result.is24HoursOpen = false;
            }
            formattedHours[day] = result;
          }
        }
        updatedForm.hours = formattedHours;
      }
    }

    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      setForm(updatedForm);
      return true;
    }
    const firstErrorField = Object.keys(errors)[0];
    const el = inputRefs.current[firstErrorField];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus?.();
    }

    return false;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
    if (name === "logo") {
      const file = files[0];
      const maxSize = 1 * 1024 * 1024;
      if (
        file &&
        !["image/png", "image/jpeg", "image/jpg"].includes(file.type)
      ) {
        setMessage("❌ Only PNG or JPG files are allowed for logo.");
        setForm({ ...form, logo: null, logoPreview: null });
        return;
      }
      if (file.size > maxSize) {
        setMessage("❌ Logo must be less than 1MB.");
        setForm({ ...form, logo: null, logoPreview: null });
        return;
      }
      setForm({ ...form, logo: file, logoPreview: URL.createObjectURL(file) });
    } else if (name === "pincode") {
      setForm({ ...form, pincode: value });
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(async () => {
        if (value.length >= 3) {
          try {
            const res = await axios(
              `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(
                value
              )}&apiKey=${process.env.REACT_APP_GEOAPIFY}`
            );
            const data = res.data;
            if (data.features?.length > 0) {
              const { city, state, country } = data.features[0].properties;
              setForm((prev) => ({
                ...prev,
                city: city || "",
                state: state || "",
                country: country || "",
              }));
            }
          } catch (err) {
            console.error("Geoapify error:", err);
          }
        }
      }, 1000);
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const saveToSession = (data) => {
    try {
      sessionStorage.setItem("restaurantForm", JSON.stringify(data));
    } catch (e) {
      console.warn("⚠️ Failed to save to sessionStorage:", e);
    }
  };

  const handleNext = () => {
    document.activeElement?.blur();
    if (!validateStep(step)) {
      toast.error("Please fill all required fields.");
      return;
    }
    saveToSession(form);
    setStep((prev) => prev + 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) {
      toast.error("Please fill all required fields.");
      return;
    }
    setIsSubmitting(true);
    toast.info("Submitting...");
    const formData = new FormData();
    const processedHours = {};
    for (const [day, config] of Object.entries(form.hours || {})) {
      if (config.open) {
        processedHours[day] = {
          open: true,
          is24HoursOpen: config.is24HoursOpen,
        };
        if (!config.is24HoursOpen) {
          processedHours[
            day
          ].openingTime = `${config.openingTime.hour}:${config.openingTime.minute} ${config.openingTime.period}`;
          processedHours[
            day
          ].closingTime = `${config.closingTime.hour}:${config.closingTime.minute} ${config.closingTime.period}`;
        }
      }
    }

    Object.entries(form).forEach(([key, val]) => {
      if (key === "cuisineTypes") {
        val.forEach((item) => formData.append("cuisineTypes", item));
      } else if (key === "hours") {
        formData.append("hours", JSON.stringify(processedHours));
      } else if (key !== "logoPreview") {
        formData.append(key, val);
      }
    });

    const logoRemoved = !form.logo && !form.logoPreview;
    formData.append("logoRemoved", logoRemoved);

    try {
      if (restaurantId) {
        await axios.put(`${API_BASE}/api/resturant/${restaurantId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("✅ Updated successfully!");
        sessionStorage.removeItem("restaurantForm");

        setTimeout(() => {
          navigate("/resturant/details");
        }, 1000);
      } else {
        await axios.post(`${API_BASE}/api/resturant/addResturant`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("✅ Registered successfully!");
      }

      sessionStorage.removeItem("restaurantForm");
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.dismiss();
      toast.error("❌ Server Error. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-container scrollable-form">
      <h2>Register Restaurant/Store</h2>
      <Stepper activeStep={step} onStepChange={(e) => setStep(e.index)} linear>
        <StepperPanel header="Basic Information">
          <div className="form-grid">
            <div className="form-group">
              <label>Name</label>
              <InputText
                name="name"
                value={form.name}
                onChange={handleChange}
                className={formErrors.name && "p-invalid"}
                ref={(el) => (inputRefs.current["name"] = el)}
              />
              {formErrors.name && (
                <small className="p-error">{formErrors.name}</small>
              )}
            </div>
            <div className="form-group">
              <label>Category</label>
              <Dropdown
                name="category"
                value={form.category}
                options={categories.map((c) => ({
                  label: c.name,
                  value: c.name,
                }))}
                onChange={(e) =>
                  handleChange({ target: { name: "category", value: e.value } })
                }
                placeholder="Select Category"
                filter
                className={formErrors.category && "p-invalid"}
                ref={(el) => (inputRefs.current["category"] = el)}
              />
              {formErrors.category && (
                <small className="p-error">{formErrors.category}</small>
              )}
            </div>
            <div className="form-group">
              <label>Cuisine Types</label>
              <MultiSelect
                name="cuisineTypes"
                value={form.cuisineTypes}
                options={cuisines.map((c) => ({
                  label: c.name,
                  value: c.name,
                }))}
                onChange={(e) =>
                  handleChange({
                    target: { name: "cuisineTypes", value: e.value },
                  })
                }
                display="chip"
                filter
                className={formErrors.cuisineTypes && "p-invalid"}
                ref={(el) => (inputRefs.current["cuisineTypes"] = el)}
              />
              {formErrors.cuisineTypes && (
                <small className="p-error">{formErrors.cuisineTypes}</small>
              )}
            </div>
            <div className="form-group" style={{ gridColumn: "span 3" }}>
              <label>Description (Optional)</label>
              <InputTextarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={3}
                autoResize
              />
            </div>
            <div className="form-group">
              <label>License Number (Optional)</label>
              <InputText
                name="licenseNumber"
                value={form.licenseNumber}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>GST Number (Optional)</label>
              <InputText
                name="gstNumber"
                value={form.gstNumber}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <Dropdown
                name="status"
                value={form.status}
                options={[
                  { label: "Active", value: "Active" },
                  { label: "Inactive", value: "Inactive" },
                ]}
                onChange={(e) =>
                  handleChange({ target: { name: "status", value: e.value } })
                }
              />
            </div>
            <div className="form-group" style={{ gridColumn: "span 3" }}>
              <label>Logo (Optional)</label>
              <FileUpload
                ref={logoUploadRef}
                name="logo"
                mode="basic"
                accept=".jpg,.png,.jpeg"
                chooseLabel="Upload Logo"
                customUpload
                auto
                uploadHandler={(e) =>
                  handleChange({ target: { name: "logo", files: e.files } })
                }
              />
              {form.logoPreview && (
                <div className="logo-preview-container">
                  <img
                    src={form.logoPreview}
                    alt="Logo Preview"
                    className="logo-preview"
                  />
                  <button
                    type="button"
                    className="logo-remove-button"
                    onClick={() => {
                      setForm({ ...form, logo: null, logoPreview: null });
                      setLogoRemoved(true);
                      logoUploadRef.current.clear();
                    }}
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>
          </div>
          <Button
            label="Next"
            onClick={handleNext}
            style={{ display: "block", margin: "2rem auto" }}
          />
        </StepperPanel>

        <StepperPanel header="Location & Online Presence">
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: "span 3" }}>
              <label>Address</label>
              <InputTextarea
                name="address"
                value={form.address}
                onChange={handleChange}
                rows={3}
                autoResize
                className={formErrors.address && "p-invalid"}
                ref={(el) => (inputRefs.current["address"] = el)}
              />
              {formErrors.address && (
                <small className="p-error">{formErrors.address}</small>
              )}
            </div>
            {["pincode", "city", "state", "country"].map((field) => (
              <div className="form-group" key={field}>
                <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                <InputText
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  className={formErrors[field] && "p-invalid"}
                  ref={(el) => (inputRefs.current[field] = el)}
                />
                {formErrors[field] && (
                  <small className="p-error">{formErrors[field]}</small>
                )}
              </div>
            ))}
            <div className="form-group">
              <label>Coordinates (Optional)</label>
              <InputText
                name="coordinates"
                value={form.coordinates}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Website (Optional)</label>
              <InputText
                name="website"
                value={form.website}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Social Media (Optional)</label>
              <InputText
                name="socialLinks"
                value={form.socialLinks}
                onChange={handleChange}
              />
            </div>
          </div>
          <Button
            label="Next"
            onClick={handleNext}
            style={{ display: "block", margin: "2rem auto" }}
          />
        </StepperPanel>

        <StepperPanel header="Contact Details">
          <div className="form-grid">
            {["contactPerson", "phone", "email"].map((field) => (
              <div className="form-group" key={field}>
                <label>
                  {field === "contactPerson"
                    ? "Contact Person"
                    : field.charAt(0).toUpperCase() + field.slice(1)}
                </label>
                <InputText
                  name={field}
                  value={form[field]}
                  onChange={handleChange}
                  className={formErrors[field] && "p-invalid"}
                  ref={(el) => (inputRefs.current[field] = el)}
                />
                {formErrors[field] && (
                  <small className="p-error">{formErrors[field]}</small>
                )}
              </div>
            ))}

            <div className="form-group" style={{ gridColumn: "span 3" }}>
              <h4 className="mt-4 mb-3">Weekly Operating Hours</h4>
              {[
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ].map((day) => {
                const isOpen = form.hours?.[day]?.open || false;
                const is24 = form.hours?.[day]?.is24HoursOpen || false;
                const opening = form.hours?.[day]?.openingTime || {
                  hour: "09",
                  minute: "00",
                  period: "AM",
                };
                const closing = form.hours?.[day]?.closingTime || {
                  hour: "06",
                  minute: "00",
                  period: "PM",
                };

                return (
                  <div
                    key={day}
                    className="day-toggle-group"
                    style={{ marginBottom: "1.5rem" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                      }}
                    >
                      <label style={{ minWidth: "80px" }}>{day}</label>
                      <ToggleButton
                        onLabel="Open"
                        offLabel="Closed"
                        checked={isOpen}
                        onChange={(e) => {
                          const updatedDay = {
                            ...form.hours?.[day],
                            open: e.value,
                            is24HoursOpen: false,
                          };
                          if (e.value) {
                            updatedDay.openingTime = opening;
                            updatedDay.closingTime = closing;
                          } else {
                            delete updatedDay.openingTime;
                            delete updatedDay.closingTime;
                            updatedDay.is24HoursOpen = false;
                          }
                          setForm({
                            ...form,
                            hours: {
                              ...form.hours,
                              [day]: updatedDay,
                            },
                          });
                        }}
                      />
                    </div>
                    {isOpen && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                        }}
                      >
                        <Checkbox
                          inputId={`${day}-24hours`}
                          checked={is24}
                          onChange={(e) => {
                            const updated = {
                              ...form.hours,
                              [day]: {
                                ...form.hours[day],
                                is24HoursOpen: e.checked,
                              },
                            };
                            setForm({ ...form, hours: updated });
                          }}
                        />
                        <p htmlFor={`${day}-24hours`} className="ml-2">
                          24 Hours Open
                        </p>
                      </div>
                    )}
                    {isOpen && !is24 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "2rem",
                          alignItems: "center",
                        }}
                      >
                        {["opening", "closing"].map((type) => {
                          const time = type === "opening" ? opening : closing;
                          const fieldKey =
                            type === "opening" ? "openingTime" : "closingTime";
                          return (
                            <div key={type} className="form-group-inline">
                              <div className="form-group-inline-responsive">
                                <p style={{ minWidth: "60px" }}>
                                  {type.charAt(0).toUpperCase() + type.slice(1)}
                                </p>
                              </div>
                              <Dropdown
                                value={time.hour}
                                options={[...Array(12).keys()].map((h) => ({
                                  label: String(h + 1).padStart(2, "0"),
                                  value: String(h + 1).padStart(2, "0"),
                                }))}
                                onChange={(e) => {
                                  const updated = { ...form.hours };
                                  updated[day][fieldKey] = {
                                    ...time,
                                    hour: e.value,
                                  };
                                  setForm({ ...form, hours: updated });
                                }}
                                placeholder="Hour"
                                className="w-full md:w-8rem"
                              />
                              <Dropdown
                                value={time.minute}
                                options={["00", "30"].map((m) => ({
                                  label: m,
                                  value: m,
                                }))}
                                onChange={(e) => {
                                  const updated = { ...form.hours };
                                  updated[day][fieldKey] = {
                                    ...time,
                                    minute: e.value,
                                  };
                                  setForm({ ...form, hours: updated });
                                }}
                                placeholder="Minute"
                                className="w-full md:w-6rem"
                              />
                              <Dropdown
                                value={time.period}
                                options={["AM", "PM"].map((p) => ({
                                  label: p,
                                  value: p,
                                }))}
                                onChange={(e) => {
                                  const updated = { ...form.hours };
                                  updated[day][fieldKey] = {
                                    ...time,
                                    period: e.value,
                                  };
                                  setForm({ ...form, hours: updated });
                                }}
                                placeholder="AM/PM"
                                className="w-full md:w-6rem"
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>{" "}
          {/* End .form-grid */}
          <div className="form-footer">
            <Button
              label={isSubmitting ? "Submitting..." : "Submit"}
              onClick={handleSubmit}
              disabled={isSubmitting}
            />
            {message && (
              <div className="mt-3">
                <Message
                  severity={message.startsWith("✅") ? "success" : "error"}
                  text={message}
                />
              </div>
            )}
          </div>
        </StepperPanel>
      </Stepper>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}
